import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function AdminPayersReport() {
  const { t } = useTranslation();

  const [payers, setPayers] = useState([]);
  const [textFilter, setTextFilter] = useState("");
  const [ticketBalanceFilter, setTicketBalanceFilter] = useState("");

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchPayers();
  }, []);

  const fetchPayers = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/payers`);
      setPayers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch admin payers report", err);
      setPayers([]);
      setMessage("❌ " + t("admin_payers_report.messages.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const formatMoney = (value) =>
    `${Number(value || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} TL`;

  const translateStatus = (status) => {
    if (!status) return "-";
    return t(`admin_payers_report.status_values.${status}`, status);
  };

  const getText = (value) => (value == null ? "" : String(value).toLowerCase());

  const filteredPayers = payers.filter((p) => {
    const search = textFilter.toLowerCase().trim();

    const textMatch =
      search === "" ||
      getText(p.user_id).includes(search) ||
      getText(p.userno).includes(search) ||
      getText(p.fullname).includes(search) ||
      getText(p.route_id).includes(search) ||
      getText(p.route_name).includes(search) ||
      getText(p.start_point).includes(search) ||
      getText(p.end_point).includes(search) ||
      getText(p.wallet_notes).includes(search) ||
      getText(p.sponsor).includes(search) ||
      getText(p.profile_notes).includes(search) ||
      getText(p.user_tax_id).includes(search) ||
      getText(p.email).includes(search) ||
      getText(p.wallet_id).includes(search) ||
      getText(p.walletname).includes(search) ||
      getText(p.status).includes(search);

    let ticketBalanceMatch = true;

    if (ticketBalanceFilter.trim() !== "") {
      const parts = ticketBalanceFilter.split("-").map((v) => v.trim());

      if (parts.length === 2) {
        const min = parseFloat(parts[0]);
        const max = parseFloat(parts[1]);

        if (!Number.isNaN(min) && !Number.isNaN(max)) {
          ticketBalanceMatch =
            Number(p.ticket_balance || 0) >= min &&
            Number(p.ticket_balance || 0) <= max;
        }
      }
    }

    return textMatch && ticketBalanceMatch;
  });

  return (
    <StandardScreen
      topNav={<TopNav title={t("admin_payers_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("admin_payers_report.title")}>
              <Stack gap="3">
                <FormField label={t("admin_payers_report.fields.text_filter.label")}>
                  <TextInput
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                    placeholder={t("admin_payers_report.fields.text_filter.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_payers_report.fields.ticket_balance_filter.label")}>
                  <TextInput
                    value={ticketBalanceFilter}
                    onChange={(e) => setTicketBalanceFilter(e.target.value)}
                    placeholder={t("admin_payers_report.fields.ticket_balance_filter.placeholder")}
                  />
                </FormField>
              </Stack>
            </Card>

            <StatusMessage
              message={
                loading
                  ? t("admin_payers_report.messages.loading")
                  : message
              }
            />

            <Card>
              <TableShell maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>
                      <Th columnIndex={0} sticky wider>
                        {t("admin_payers_report.table.user_id")}
                      </Th>

                      <Th columnIndex={1} sticky wider>
                        {t("admin_payers_report.table.userno")}
                      </Th>

                      <Th wider>{t("admin_payers_report.table.fullname")}</Th>
                      <Th wider>{t("admin_payers_report.table.route")}</Th>
                      <Th wider>{t("admin_payers_report.table.start_point")}</Th>
                      <Th wider>{t("admin_payers_report.table.end_point")}</Th>
                      <Th wider>{t("admin_payers_report.table.sponsor")}</Th>
                      <Th wider>{t("admin_payers_report.table.user_tax_id")}</Th>
                      <Th wider>{t("admin_payers_report.table.email")}</Th>

                      <Th wider align="right">
                        {t("admin_payers_report.table.money_balance")}
                      </Th>

                      <Th wider align="right">
                        {t("admin_payers_report.table.ticket_balance")}
                      </Th>

                      <Th wider>{t("admin_payers_report.table.status")}</Th>
                      <Th wider>{t("admin_payers_report.table.wallet_notes")}</Th>
                      <Th wider>{t("admin_payers_report.table.profile_notes")}</Th>
                      <Th wider>{t("admin_payers_report.table.created_at")}</Th>
                      <Th>{t("admin_payers_report.table.wallet")}</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredPayers.map((p) => (
                      <tr key={`${p.user_id}-${p.wallet_id}`}>
                        <Td columnIndex={0} sticky>
                          {p.user_id}
                        </Td>

                        <Td columnIndex={1} sticky wider>
                          {p.userno || "-"}
                        </Td>

                        <Td wrap>{p.fullname || "-"}</Td>

                        <Td>
                          {p.route_name
                            ? `#${p.route_id} - ${p.route_name}`
                            : p.route_id
                            ? `#${p.route_id}`
                            : "-"}
                        </Td>

                        <Td>{p.start_point || "-"}</Td>
                        <Td>{p.end_point || "-"}</Td>
                        <Td>{p.sponsor || "-"}</Td>
                        <Td>{p.user_tax_id || "-"}</Td>
                        <Td>{p.email || "-"}</Td>

                        <NumberTd>{formatMoney(p.money_balance)}</NumberTd>
                        <NumberTd>{p.ticket_balance ?? 0}</NumberTd>

                        <Td>{translateStatus(p.status)}</Td>
                        <Td>{p.wallet_notes || "-"}</Td>
                        <Td>{p.profile_notes || "-"}</Td>
                        <Td>{formatDateTime(p.created_at)}</Td>

                        <Td>
                          {t("admin_payers_report.table.wallet_prefix")} #{p.wallet_id} -{" "}
                          {p.walletname || "-"}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableShell>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}